import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getNotifications, markAllNotifRead, markNotifRead, type NotifRow } from '../api';
import { useAuth } from '../auth';

export default function NotificationBell() {
  const { user } = useAuth();
  if (!user) return null;
  return <Bell />;
}

function Bell() {
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unread = data?.unread ?? 0;

  return (
    <div ref={wrap} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg bg-ink-800 text-chalk hover:bg-ink-700 active:bg-ink-600"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        title="Notifications"
      >
        <BellIcon />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 grid place-items-center text-[10px] font-bold leading-none rounded-full bg-red-500 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && <Panel items={data?.items ?? []} onClose={() => setOpen(false)} />}
    </div>
  );
}

function Panel({ items, onClose }: { items: NotifRow[]; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const markAll = useMutation({
    mutationFn: markAllNotifRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markOne = useMutation({
    mutationFn: markNotifRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  function onClickItem(n: NotifRow) {
    if (!n.read_at) markOne.mutate(n.id);
    onClose();
    if (n.link) navigate(n.link);
  }

  return (
    <div className="absolute right-0 mt-1 z-40 w-[20rem] sm:w-[22rem] max-w-[calc(100vw-1rem)] card shadow-glow overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-ink-800">
        <div className="text-xs uppercase tracking-widest text-dim">Notifications</div>
        {items.some((n) => !n.read_at) && (
          <button
            onClick={() => markAll.mutate()}
            className="text-[11px] text-pitch-400 hover:underline"
          >Mark all read</button>
        )}
      </div>
      <div className="max-h-[70vh] overflow-y-auto divide-y divide-ink-800/60">
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-dim">Nothing yet.</div>
        ) : items.map((n) => (
          <button
            key={n.id}
            onClick={() => onClickItem(n)}
            className={`w-full text-left px-3 py-2.5 hover:bg-ink-800 active:bg-ink-700 transition-colors ${
              n.read_at ? 'opacity-55' : ''
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <KindPill kind={n.kind} />
              <div className="flex items-center gap-1.5 text-[10px] text-dim">
                {!n.read_at && <span className="w-1.5 h-1.5 rounded-full bg-pitch-400" />}
                <span>{relativeTime(n.created_at)}</span>
              </div>
            </div>
            <div className="text-sm leading-tight font-medium text-chalk">{n.title}</div>
            {n.body && <div className="text-xs text-dim mt-0.5 line-clamp-2">{n.body}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

const KIND_META: Record<string, { label: string; cls: string }> = {
  bet_invited:   { label: 'BET · INVITE',    cls: 'bg-pitch-500/15 text-pitch-400 ring-pitch-500/30' },
  bet_accepted:  { label: 'BET · ACCEPTED',  cls: 'bg-pitch-500/15 text-pitch-400 ring-pitch-500/30' },
  bet_declined:  { label: 'BET · DECLINED',  cls: 'bg-red-500/15 text-red-400 ring-red-500/30' },
  bet_cancelled: { label: 'BET · CANCELLED', cls: 'bg-red-500/15 text-red-400 ring-red-500/30' },
  bet_resolved:  { label: 'BET · SETTLED',   cls: 'bg-amber-500/15 text-amber-400 ring-amber-500/30' },
  match_kickoff: { label: 'MATCH · KICKOFF', cls: 'bg-sky-500/15 text-sky-400 ring-sky-500/30' },
  match_ended:   { label: 'MATCH · FT',      cls: 'bg-sky-500/15 text-sky-400 ring-sky-500/30' },
  team_advanced: { label: 'TEAM · ADVANCED', cls: 'bg-purple-500/15 text-purple-400 ring-purple-500/30' },
  news:          { label: 'NEWS',            cls: 'bg-cyan-500/15 text-cyan-400 ring-cyan-500/30' },
  announcement:  { label: 'ANNOUNCE',        cls: 'bg-fuchsia-500/15 text-fuchsia-400 ring-fuchsia-500/30' },
};

function KindPill({ kind }: { kind: string }) {
  const v = KIND_META[kind] ?? { label: kind.toUpperCase(), cls: 'bg-ink-700 text-dim ring-ink-600' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase ring-1 ${v.cls}`}>
      {v.label}
    </span>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}
